import type { ActionInput, ActionName, ActionOutput, ActionResponse, CaptureResult, Me } from '@zibanu/shared';
import type { BlobLike, FetchLike, FormDataCtor, FormDataLike, ResponseLike, StorageLike } from './types';

/** No connection (or the request never got an answer). Retry later; nothing was lost. */
export class OfflineError extends Error {
  constructor(cause?: unknown) {
    super('offline');
    this.name = 'OfflineError';
    if (cause !== undefined) (this as { cause?: unknown }).cause = cause;
  }
}

export interface ClientOptions {
  /** The API's origin, e.g. https://api.zibanu.ng. */
  baseUrl: string;
  fetch: FetchLike;
  FormData: FormDataCtor;
  storage: StorageLike;
  /** What x-zibanu-client says; the server only checks it is there. */
  clientName?: string;
}

export interface ZibanuClient {
  call<N extends ActionName>(name: N, input: ActionInput<N>, orgId?: string): Promise<ActionResponse<ActionOutput<N>>>;
  me(): Promise<ActionResponse<Me>>;
  auth: {
    requestCode(phone: string): Promise<ActionResponse<{ sent: true }>>;
    verifyCode(phone: string, code: string, deviceLabel?: string): Promise<ActionResponse<Me>>;
    redeemLink(token: string, deviceLabel?: string): Promise<ActionResponse<Me>>;
    logout(): Promise<void>;
  };
  capture: {
    text(key: string, text: string, orgId: string): Promise<ActionResponse<CaptureResult>>;
    media(key: string, file: BlobLike, fileName: string, caption: string | undefined, orgId: string): Promise<ActionResponse<CaptureResult>>;
    quickReply(key: string, text: string, orgId: string): Promise<ActionResponse<CaptureResult>>;
    /** A payment against a customer: JSON, or multipart when a screenshot goes with it. */
    payment(
      key: string,
      p: { counterpartyId: string; amountMinor: number; method: 'cash' | 'transfer' | 'pos' },
      orgId: string,
      file?: { blob: BlobLike; name: string },
    ): Promise<ActionResponse<CaptureResult>>;
  };
  /** One photo or voice note of an entry's evidence; null when it isn't there for this person. */
  media(eventId: string, index: number, orgId: string): Promise<BlobLike | null>;
  /** The books this device opened last. */
  preferredOrg: { get(): Promise<string | null>; set(orgId: string | null): Promise<void> };
  /** Who this device last served: a different person logging in wipes what the last one left (spec 3A §6.2). */
  lastPerson: { get(): Promise<string | null>; set(personId: string | null): Promise<void> };
  onSignedOut(listener: () => void): () => void;
}

const PREFERRED_ORG = 'prefs:org';
const LAST_PERSON = 'zibanu:last-person';
const LOGOUT_PENDING = 'zibanu:logout-pending';
const INTERNAL = { ok: false, error: 'internal' } as const;

export function createClient(opts: ClientOptions): ZibanuClient {
  const base = opts.baseUrl.replace(/\/+$/, '');
  const clientHeader = { 'x-zibanu-client': opts.clientName ?? 'web' };
  const listeners = new Set<() => void>();

  const send = async (path: string, init: { method: string; headers?: Record<string, string>; body?: string | FormDataLike }) => {
    try {
      return await opts.fetch(`${base}/api/v1${path}`, { ...init, credentials: 'include' });
    } catch (err) {
      throw new OfflineError(err);
    }
  };

  /** Every answer is the server's own {ok, …} body; anything else is our failure, never details. */
  const read = async <T>(res: ResponseLike): Promise<ActionResponse<T>> => {
    let body: unknown;
    try {
      body = await res.json();
    } catch {
      return INTERNAL;
    }
    if (typeof body !== 'object' || body === null || typeof (body as { ok?: unknown }).ok !== 'boolean') return INTERNAL;
    const answer = body as ActionResponse<T>;
    if (!answer.ok && answer.error === 'unauthenticated') for (const l of [...listeners]) l();
    return answer;
  };

  const postJson = async <T>(path: string, body: unknown, orgId?: string) =>
    read<T>(
      await send(path, {
        method: 'POST',
        headers: { 'content-type': 'application/json', ...clientHeader, ...(orgId ? { 'x-org-id': orgId } : {}) },
        body: JSON.stringify(body ?? {}),
      }),
    );

  /**
   * A logout the server never heard (spec 3A Review Focus 5): the HttpOnly cookie only the server can clear is still live, so the marker stays until it answers.
   * 401 means the session is gone already.
   */
  const logout = async () => {
    await opts.storage.set(LOGOUT_PENDING, true);
    const res = await postJson('/auth/logout', {});
    if (res.ok || res.error === 'unauthenticated') await opts.storage.delete(LOGOUT_PENDING);
  };
  /** Before any login: finish a pending logout if there is one, best effort. */
  const finishPending = async () => {
    if (!(await opts.storage.get(LOGOUT_PENDING))) return;
    try {
      await logout();
    } catch {
      // Still offline or erroring: the login below replaces the cookie anyway.
    }
  };
  const loggedIn = async (res: ActionResponse<Me>) => {
    // The new session's cookie has replaced the old one on this device; a later pending logout must not end it.
    if (res.ok) await opts.storage.delete(LOGOUT_PENDING);
    return res;
  };

  return {
    call: (name, input, orgId) => postJson(`/actions/${name}`, input, orgId),
    async me() {
      if (await opts.storage.get(LOGOUT_PENDING)) {
        // The old session is never trusted, whether or not the server could be told.
        try {
          await logout();
        } catch {
          // Offline: the marker stays for the next try.
        }
        return { ok: false, error: 'unauthenticated' };
      }
      return read<Me>(await send('/me', { method: 'GET', headers: { ...clientHeader } }));
    },
    auth: {
      requestCode: (phone) => postJson('/auth/code/request', { phone }),
      async verifyCode(phone, code, deviceLabel) {
        await finishPending();
        return loggedIn(await postJson<Me>('/auth/code/verify', { phone, code, ...(deviceLabel ? { deviceLabel } : {}) }));
      },
      async redeemLink(token, deviceLabel) {
        await finishPending();
        return loggedIn(await postJson<Me>('/auth/link', { token, ...(deviceLabel ? { deviceLabel } : {}) }));
      },
      logout,
    },
    capture: {
      text: (key, text, orgId) => postJson('/capture', { idempotencyKey: key, text }, orgId),
      quickReply: (key, text, orgId) => postJson('/quick-reply', { idempotencyKey: key, text }, orgId),
      async payment(key, p, orgId, file) {
        if (!file) return postJson('/capture/payment', { idempotencyKey: key, ...p }, orgId);
        const form = new opts.FormData();
        form.append('idempotencyKey', key);
        form.append('counterpartyId', p.counterpartyId);
        form.append('amountMinor', String(p.amountMinor));
        form.append('method', p.method);
        form.append('file', file.blob, file.name);
        return read<CaptureResult>(await send('/capture/payment', { method: 'POST', headers: { ...clientHeader, 'x-org-id': orgId }, body: form }));
      },
      async media(key, file, fileName, caption, orgId) {
        const form = new opts.FormData();
        form.append('idempotencyKey', key);
        if (caption) form.append('caption', caption);
        form.append('file', file, fileName);
        // No content type of our own: the platform sets the multipart boundary.
        return read<CaptureResult>(await send('/capture', { method: 'POST', headers: { ...clientHeader, 'x-org-id': orgId }, body: form }));
      },
    },
    async media(eventId, index, orgId) {
      const res = await send(`/media/${encodeURIComponent(eventId)}/${index}`, {
        method: 'GET',
        headers: { ...clientHeader, 'x-org-id': orgId },
      });
      if (res.status === 401) {
        await read(res);
        return null;
      }
      return res.ok ? res.blob() : null;
    },
    preferredOrg: {
      async get() {
        return (await opts.storage.get<string>(PREFERRED_ORG)) ?? null;
      },
      async set(orgId) {
        if (orgId) await opts.storage.set(PREFERRED_ORG, orgId);
        else await opts.storage.delete(PREFERRED_ORG);
      },
    },
    lastPerson: {
      async get() {
        return (await opts.storage.get<string>(LAST_PERSON)) ?? null;
      },
      async set(personId) {
        if (personId) await opts.storage.set(LAST_PERSON, personId);
        else await opts.storage.delete(LAST_PERSON);
      },
    },
    onSignedOut(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}
